import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CalendarClock, ClipboardList, LayoutList, ChevronLeft, ChevronRight, Plus, PoundSterling, Sparkles, Trash2, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import DetailDrawer from "@/components/DetailDrawer";
import BookingDrawer from "@/components/bookings/BookingDrawer";
import { fmtCents, fmtTime, mondayOf, addDays, isoDate, fmtDayLabel, fmtDuration } from "@/lib/time";
import { toast } from "sonner";
import { Skeleton, SkeletonTable, SkeletonCard } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";

export default function Bookings() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "week";
  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1400px]" data-testid="bookings-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center">
          <CalendarClock size={17}/>
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Bookings</div>
          <div className="text-[11.5px] text-brand-muted">Appointments, capacity, allocations. One booking, one truth.</div>
        </div>
      </div>
      <div className="flex items-center gap-1 border-b border-brand-rule mb-4">
        <Tab v="week" cur={tab} setV={(v)=>setParams({tab:v})} icon={<CalendarClock size={13}/>}>Calendar</Tab>
        <Tab v="list" cur={tab} setV={(v)=>setParams({tab:v})} icon={<LayoutList size={13}/>}>List</Tab>
        <Tab v="services" cur={tab} setV={(v)=>setParams({tab:v})} icon={<ClipboardList size={13}/>}>Services</Tab>
      </div>
      {tab === "week" && <BookingBoard variant="calendar"/>}
      {tab === "list" && <BookingBoard variant="list"/>}
      {tab === "services" && <ServicesTab/>}
    </div>
  );
}

function Tab({ v, cur, setV, children, icon }) {
  const active = v === cur;
  return (
    <button onClick={() => setV(v)}
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 flex items-center gap-1.5 ${active ? 'border-brand-violet text-brand-violet' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}
            data-testid={`bookings-tab-${v}`}>
      {icon}{children}
    </button>
  );
}

// ---------------- BOOKING BOARD (calendar + list share this fetcher) ----------------

const STATUS_STYLES = {
  pending:   { chip: "chip-amber",   label: "pending",   ring: "border-amber-300 bg-amber-50 text-amber-800" },
  confirmed: { chip: "chip-violet",  label: "confirmed", ring: "border-brand-violet/30 bg-brand-violet/5 text-brand-violet-lo" },
  completed: { chip: "chip-emerald", label: "completed", ring: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  cancelled: { chip: "chip-coral",   label: "cancelled", ring: "border-red-200 bg-red-50 text-red-700 line-through" },
  no_show:   { chip: "chip-coral",   label: "no-show",   ring: "border-red-200 bg-red-50 text-red-700" },
};

function BookingBoard({ variant }) {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("bookings.manage");
  const [sites, setSites] = React.useState([]);
  const [siteId, setSiteId] = React.useState("");
  const [weekMonday, setWeekMonday] = React.useState(mondayOf());
  const [rows, setRows] = React.useState(null);
  const [drawer, setDrawer] = React.useState(null); // {kind:'new'|'edit', booking, day_iso, hour}
  const [services, setServices] = React.useState([]);
  const [members, setMembers] = React.useState([]);

  const weekStartIso = isoDate(weekMonday);
  const days = React.useMemo(() => Array.from({length:7}, (_, i) => addDays(weekMonday, i)), [weekMonday]);

  React.useEffect(() => {
    api.get(`/companies/${companyId}/sites`).then(({data}) => {
      setSites(data.sites);
      if (!siteId && data.sites.length) setSiteId(data.sites[0].id);
    });
    api.get(`/companies/${companyId}/bookings/services`, { params: { active: true } })
      .then(({data}) => setServices(data.services));
    api.get(`/companies/${companyId}/members`).then(({data}) => setMembers(data.members));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const start = weekMonday.toISOString();
      const end = addDays(weekMonday, 7).toISOString();
      const { data } = await api.get(`/companies/${companyId}/bookings`, {
        params: { site_id: siteId, start, end },
      });
      setRows(data.bookings);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId, siteId, weekMonday]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type;
      if (t && (t.startsWith("booking.") || t.startsWith("service."))) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  const byDay = React.useMemo(() => {
    const map = {};
    for (const b of (rows || [])) {
      const key = b.starts_at.slice(0, 10);
      map[key] = map[key] || [];
      map[key].push(b);
    }
    for (const k of Object.keys(map)) map[k].sort((a,b) => a.starts_at.localeCompare(b.starts_at));
    return map;
  }, [rows]);

  const total = (rows || []).length;
  const confirmedCount = (rows || []).filter(b => b.status === "confirmed").length;
  const pendingCount = (rows || []).filter(b => b.status === "pending").length;
  const totalPence = (rows || []).filter(b => b.status !== "cancelled" && b.status !== "no_show")
                                 .reduce((a, b) => a + (b.service_price_cents || 0), 0);

  return (
    <div data-testid={`bookings-${variant}`}>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <select className="field-input !w-56 !h-8" value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                data-testid="bookings-site-select">
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="flex items-center gap-1 rounded-md border border-brand-rule bg-card">
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, -7))}
                  data-testid="bookings-prev-week"><ChevronLeft size={14}/></button>
          <button className="btn-ghost !h-8 !px-2 mono-num text-[11px]"
                  onClick={() => setWeekMonday(mondayOf())}
                  data-testid="bookings-today">
            Week of {weekStartIso}
          </button>
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, 7))}
                  data-testid="bookings-next-week"><ChevronRight size={14}/></button>
        </div>
        <div className="flex-1"/>
        {canManage && (
          <button className="btn-violet" onClick={() => setDrawer({ kind: "new" })}
                  data-testid="bookings-new">
            <Plus size={13}/> New booking
          </button>
        )}
      </div>

      {/* Summary strip */}
      <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bookings-summary">
        <SumTile label="Bookings" value={total}/>
        <SumTile label="Confirmed" value={confirmedCount}/>
        <SumTile label="Pending" value={pendingCount} tone={pendingCount>0?'amber':''}/>
        <SumTile label="Revenue (planned)" value={fmtCents(totalPence)} testid="bookings-rev"/>
      </div>

      {rows === null ? (
        <div className="card-panel p-3 space-y-2" data-testid="bookings-loading">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          eyebrow="Bookings"
          title={variant === "calendar" ? "No bookings this week." : "No bookings to show."}
          body="When a booking is created (from the app or the widget) it shows up here."
          action={canManage
            ? <button className="btn-violet" onClick={() => setDrawer({ kind: "new" })} data-testid="bookings-empty-new">
                <Plus size={13} /> New booking
              </button>
            : null}
          testid="bookings-empty"
        />
      ) : variant === "calendar" ? (
        <CalendarView days={days} byDay={byDay} onOpen={(b) => setDrawer({ kind: "edit", booking: b })}/>
      ) : (
        <ListView rows={rows} onOpen={(b) => setDrawer({ kind: "edit", booking: b })}/>
      )}

      <BookingDrawer open={!!drawer} onOpenChange={(v)=>{if(!v) setDrawer(null);}}
                     drawer={drawer} companyId={companyId} services={services}
                     members={members} sites={sites} defaultSiteId={siteId}
                     canManage={canManage} onChanged={() => load()}/>
    </div>
  );
}

function SumTile({ label, value, tone, testid }) {
  const toneClass = tone === 'amber' ? 'text-amber-700' : tone === 'coral' ? 'text-brand-coral' : 'text-brand-ink';
  return (
    <div className="card-panel px-3 py-2" data-testid={testid}>
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 mono-num text-[16px] font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}

function CalendarView({ days, byDay, onOpen }) {
  return (
    <div className="card-panel table-scroll" data-testid="bookings-calendar">
      <div className="grid" style={{gridTemplateColumns: "repeat(7, minmax(150px, 1fr))", minWidth: "1050px"}}>
        {days.map((d, i) => {
          const key = isoDate(d);
          const items = byDay[key] || [];
          return (
            <div key={key} className={`border-r border-brand-rule ${i===6?'border-r-0':''}`}>
              <div className="px-3 py-2 border-b border-brand-rule bg-brand-canvas">
                <div className="text-[10px] tracking-[0.14em] text-brand-faint uppercase">{fmtDayLabel(d)}</div>
                <div className="mono-num text-[11px] text-brand-muted">{key.slice(5)}</div>
              </div>
              <div className="p-2 space-y-1 min-h-[140px]">
                {items.map((b) => {
                  const st = STATUS_STYLES[b.status] || STATUS_STYLES.pending;
                  return (
                    <button key={b.id} onClick={() => onOpen(b)}
                            className={`w-full text-left rounded-md border px-2 py-1.5 ${st.ring} hover:shadow-soft transition`}
                            data-testid={`booking-cell-${b.id}`}>
                      <div className="mono-num text-[10.5px] font-semibold">
                        {fmtTime(b.starts_at)}–{fmtTime(b.ends_at)}
                      </div>
                      <div className="text-[12px] font-semibold truncate">{b.customer?.name}</div>
                      <div className="text-[10.5px] opacity-80 truncate">{b.service_name}{b.party_size ? ` · ${b.party_size}` : ''}</div>
                    </button>
                  );
                })}
                {items.length === 0 && <div className="text-[10.5px] text-brand-faint py-2 text-center">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ rows, onOpen }) {
  return (
    <div className="card-panel overflow-hidden">
      <div className="table-scroll">
        <table className="fable-table" data-testid="bookings-list-table">
          <thead>
            <tr>
              <th style={{width:"18%"}}>When</th>
              <th style={{width:"22%"}}>Customer</th>
              <th style={{width:"22%"}}>Service</th>
              <th style={{width:"10%"}} className="text-right">Party</th>
              <th style={{width:"14%"}} className="text-right">Price</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => {
              const st = STATUS_STYLES[b.status] || STATUS_STYLES.pending;
              return (
                <tr key={b.id} data-clickable="true" onClick={() => onOpen(b)}
                    data-testid={`bookings-row-${b.id}`}>
                  <td className="mono-num text-[12px] text-brand-ink">
                    {b.starts_at.slice(0,10)} {fmtTime(b.starts_at)}
                  </td>
                  <td className="text-[12.5px] text-brand-ink">{b.customer?.name}</td>
                  <td className="text-[12px] text-brand-muted">{b.service_name}</td>
                  <td className="mono-num text-right text-brand-muted">{b.party_size || "—"}</td>
                  <td className="mono-num text-right">{fmtCents(b.service_price_cents)}</td>
                  <td><span className={`chip ${st.chip} mono-num text-[10px]`}>{st.label}</span></td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-0">
                <EmptyState icon={LayoutList} eyebrow="Bookings"
                  title="No bookings for this site and week."
                  body="Try a different week, or create one."
                  testid="bookings-list-empty" />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- SERVICES ----------------

function ServicesTab() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("bookings.manage");
  const [rows, setRows] = React.useState(null);
  const [editing, setEditing] = React.useState(null); // service | 'new'

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/bookings/services`);
      setRows(data.services);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  async function togglePlus(s) {
    try {
      await api.patch(`/companies/${companyId}/bookings/services/${s.id}`, { listed_on_plus: !s.listed_on_plus });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }
  async function toggleActive(s) {
    try {
      await api.patch(`/companies/${companyId}/bookings/services/${s.id}`, { active: !s.active });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  if (!rows) return (
    <div className="card-panel overflow-hidden" data-testid="services-loading">
      <SkeletonTable rows={4} cols={6} />
    </div>
  );

  return (
    <div data-testid="bookings-services">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] text-brand-muted">
          <span className="mono-num text-brand-ink">{rows.length}</span> services
        </div>
        {canManage && (
          <button className="btn-violet" onClick={() => setEditing({ __new: true })}
                  data-testid="service-new">
            <Plus size={13}/> New service
          </button>
        )}
      </div>
      <div className="card-panel overflow-hidden">
        <div className="table-scroll">
          <table className="fable-table" data-testid="services-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="text-right" style={{width:"14%"}}>Duration</th>
                <th className="text-right" style={{width:"14%"}}>Price</th>
                <th style={{width:"14%"}}>Plus listing</th>
                <th style={{width:"10%"}}>Active</th>
                <th style={{width:"14%"}}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} data-testid={`service-row-${s.id}`}>
                  <td>
                    <div className="text-[12.5px] font-medium text-brand-ink">{s.name}</div>
                    <div className="text-[10.5px] text-brand-muted truncate max-w-[440px]">{s.description || '—'}</div>
                  </td>
                  <td className="mono-num text-right">{fmtDuration(s.duration_mins)}</td>
                  <td className="mono-num text-right font-semibold text-brand-ink">{fmtCents(s.price_cents)}</td>
                  <td>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={s.listed_on_plus} onChange={() => canManage && togglePlus(s)}
                             disabled={!canManage || !s.active}
                             data-testid={`service-plus-${s.id}`}/>
                      <span className={`text-[11px] ${s.listed_on_plus ? 'text-brand-teal font-semibold' : 'text-brand-muted'}`}>
                        {s.listed_on_plus ? "Listed" : "Hidden"}
                      </span>
                    </label>
                  </td>
                  <td>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={s.active} onChange={() => canManage && toggleActive(s)}
                             disabled={!canManage}
                             data-testid={`service-active-${s.id}`}/>
                      <span className={`text-[11px] ${s.active ? 'text-emerald-700 font-semibold' : 'text-brand-muted'}`}>
                        {s.active ? "Active" : "Archived"}
                      </span>
                    </label>
                  </td>
                  <td className="text-right">
                    {canManage && (
                      <button className="btn-ghost !h-7 !px-2" onClick={() => setEditing(s)}
                              data-testid={`service-edit-${s.id}`}>Edit</button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-0">
                  <EmptyState icon={Sparkles} eyebrow="Services"
                    title="No services yet."
                    body="Add your first service to enable bookings and Plus listings."
                    action={canManage
                      ? <button className="btn-violet" onClick={() => setEditing({ __new: true })}
                                data-testid="service-empty-new">
                          <Plus size={13}/> New service
                        </button>
                      : null}
                    testid="services-empty" />
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ServiceDrawer open={!!editing} onOpenChange={(v)=>{if(!v) setEditing(null);}}
                     service={editing?.__new ? null : editing} companyId={companyId}
                     onChanged={load}/>
    </div>
  );
}

function ServiceDrawer({ open, onOpenChange, service, companyId, onChanged }) {
  const [sites, setSites] = React.useState([]);
  const [f, setF] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    api.get(`/companies/${companyId}/sites`).then(({data}) => setSites(data.sites));
    setF(service ? {
      name: service.name, description: service.description || "",
      duration_mins: service.duration_mins, price_cents: service.price_cents,
      site_ids: service.site_ids || [], active: service.active,
      listed_on_plus: service.listed_on_plus,
    } : {
      name: "", description: "", duration_mins: 60, price_cents: 0,
      site_ids: [], active: true, listed_on_plus: false,
    });
  }, [open, service, companyId]);

  async function save() {
    if (busy || !f) return;
    setBusy(true);
    try {
      const payload = {
        ...f,
        duration_mins: Number(f.duration_mins),
        price_cents: Number(f.price_cents),
      };
      if (service) {
        await api.patch(`/companies/${companyId}/bookings/services/${service.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post(`/companies/${companyId}/bookings/services`, payload);
        toast.success("Created");
      }
      onChanged && onChanged();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <DetailDrawer open={open} onOpenChange={onOpenChange}
                  title={service ? service.name : "New service"}
                  subtitle={service ? "Edit service" : "Create a bookable service"}
                  testid="service-drawer">
      {!f ? null : (
        <div className="p-5 space-y-3">
          <div>
            <label className="micro-label">Name</label>
            <input className="field-input mt-1" value={f.name}
                   onChange={(e)=>setF(x=>({...x, name:e.target.value}))}
                   data-testid="service-name"/>
          </div>
          <div>
            <label className="micro-label">Description</label>
            <textarea className="field-input mt-1 !h-16" value={f.description}
                      onChange={(e)=>setF(x=>({...x, description:e.target.value}))}
                      data-testid="service-desc"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="micro-label">Duration (min)</label>
              <input type="number" min="5" step="5" className="field-input mt-1 mono-num" value={f.duration_mins}
                     onChange={(e)=>setF(x=>({...x, duration_mins:e.target.value}))}
                     data-testid="service-duration"/>
            </div>
            <div>
              <label className="micro-label">Price (pence)</label>
              <input type="number" min="0" className="field-input mt-1 mono-num" value={f.price_cents}
                     onChange={(e)=>setF(x=>({...x, price_cents:e.target.value}))}
                     data-testid="service-price"/>
            </div>
          </div>
          <div>
            <label className="micro-label">Available at sites</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {sites.map((s) => (
                <label key={s.id} className="inline-flex items-center gap-1.5 border border-brand-rule rounded-md px-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={f.site_ids.includes(s.id)}
                         onChange={(e)=>{
                           setF(x=>({...x, site_ids: e.target.checked
                             ? [...x.site_ids, s.id]
                             : x.site_ids.filter(id => id !== s.id)}));
                         }}
                         data-testid={`service-site-${s.id}`}/>
                  <span className="text-[11.5px]">{s.name}</span>
                </label>
              ))}
              {sites.length === 0 && <div className="text-[11px] text-brand-muted">No sites yet.</div>}
            </div>
            <div className="mt-1 text-[10.5px] text-brand-faint">Leave empty for site-agnostic. Otherwise, bookings will be blocked at unlisted sites.</div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={f.active}
                     onChange={(e)=>setF(x=>({...x, active:e.target.checked}))}
                     data-testid="service-active-toggle"/>
              <span className="text-[12.5px]">Active</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={f.listed_on_plus}
                     onChange={(e)=>setF(x=>({...x, listed_on_plus:e.target.checked}))}
                     data-testid="service-plus-toggle"/>
              <span className="text-[12.5px] inline-flex items-center gap-1">
                <Sparkles size={12} className="text-brand-teal"/> Listed on Plus
              </span>
            </label>
          </div>
          <div className="pt-3 border-t border-brand-rule">
            <button className="btn-violet" onClick={save} disabled={busy}
                    data-testid="service-save">
              <Save size={13}/> {busy ? "…" : (service ? "Save" : "Create")}
            </button>
          </div>
        </div>
      )}
    </DetailDrawer>
  );
}
